import React from 'react';
import { AssetUrls } from 'config/assets';
import {
  ColorsMode,
  FilterToken,
  SearchFilterState,
  normalizeColors,
} from 'screens/Main/Search/searchState';
import { finishFromTokenId } from 'utils/finishFilter';
import 'screens/Main/Search/Components/FilterTokenRow.css';
import './CardFilterTokenRow.css';

interface CardFilterTokenRowProps {
  state: SearchFilterState;
  tokens: FilterToken[];
  onChange: (patch: Partial<SearchFilterState>) => void;
  onClearToken: (token: FilterToken) => void;
  onClearAll: () => void;
  /** Horizontal scroll instead of wrapping (mobile header row). */
  scrollable?: boolean;
}

export const COLOR_PIPS: Record<string, { label: string; icon: string }> = {
  W: { label: 'White', icon: AssetUrls.search.manaSymbols.white },
  U: { label: 'Blue', icon: AssetUrls.search.manaSymbols.blue },
  B: { label: 'Black', icon: AssetUrls.search.manaSymbols.black },
  R: { label: 'Red', icon: AssetUrls.search.manaSymbols.red },
  G: { label: 'Green', icon: AssetUrls.search.manaSymbols.green },
  C: { label: 'Colorless', icon: AssetUrls.search.manaSymbols.colorless },
};

const MODE_WORD: Record<ColorsMode, string> = {
  including: 'including',
  exactly: 'exactly',
  at_most: 'at most',
};

/**
 * The active filters as removable pills above a list's results.
 *
 * Same derivation as Search's FilterTokenRow (deriveTokens), with one
 * difference the list spec asks for: the single "colors: WUB" token becomes one
 * pill per color, each with its mana pip, followed by the match mode in words.
 * The row is always rendered outside the rail, so a collapsed strip is never
 * the only place an active filter is visible.
 */
export const CardFilterTokenRow: React.FC<CardFilterTokenRowProps> = ({
  state,
  tokens,
  onChange,
  onClearToken,
  onClearAll,
  scrollable,
}) => {
  if (tokens.length === 0) return null;

  const colors = normalizeColors(state.colors);
  const otherTokens = tokens.filter((t) => t.id !== 'colors');

  const removeColor = (color: string) => {
    const next = colors.filter((c) => c !== color);
    onChange({ colors: next, colorsMode: next.length ? state.colorsMode : 'including' });
  };

  return (
    <div
      className={`filter-token-row card-filter-token-row ${scrollable ? 'scrollable' : ''}`}
      data-testid="card-filter-token-row"
    >
      {colors.map((color) => {
        const pip = COLOR_PIPS[color];
        return (
          <button
            key={`color-${color}`}
            type="button"
            className="filter-token card-filter-token--color"
            onClick={() => removeColor(color)}
            aria-label={`Remove color filter ${pip?.label ?? color}`}
          >
            {pip && <img className="card-filter-token-pip" src={pip.icon} alt="" aria-hidden="true" />}
            <span className="filter-token-label">{pip?.label ?? color}</span>
            <span className="filter-token-close" aria-hidden="true">
              ×
            </span>
          </button>
        );
      })}
      {colors.length > 0 && (
        <span className="card-filter-token-mode" data-testid="card-filter-color-mode">
          {MODE_WORD[state.colorsMode]}
        </span>
      )}

      {otherTokens.map((token) => {
        // A checked finish carries its lettered mark, as the color chips carry
        // their mana pip (offer builder Finish group, Decision 7).
        const finish = finishFromTokenId(token.id);
        return (
        <button
          key={token.id}
          type="button"
          className={`filter-token${token.id === 'setName' ? ' card-filter-token--set' : ''}${
            finish ? ' card-filter-token--finish' : ''
          }`}
          onClick={() => onClearToken(token)}
          aria-label={`Remove filter ${token.label}`}
        >
          {finish && (
            <img
              className="card-filter-token-pip"
              src={AssetUrls.icons.finish.mark[finish]}
              alt=""
              aria-hidden="true"
            />
          )}
          <span className="filter-token-label">
            {token.id === 'setName' ? state.setName.trim() : token.label}
          </span>
          <span className="filter-token-close" aria-hidden="true">
            ×
          </span>
        </button>
        );
      })}

      {tokens.length > 1 && (
        <button type="button" className="filter-token-clear-all" onClick={onClearAll}>
          Clear all
        </button>
      )}
    </div>
  );
};

export default CardFilterTokenRow;
